import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { Web3Model } from "../../../../../web3/models/Web3Model";
import { NFTGiftPackage } from "../../../../../web3/nfts/NFTGiftPackage";
import { NFTPackage, PackageType } from "../../../../../web3/nfts/NFTPackage";
import { INetworkClient } from "../../../../networking/INetworkClient";
import { RewardDto } from "../../../../networking/data/responses/RewardDto";
import { IMyWalletModel } from "./IMyWalletModel";
import { ILogger } from "../../../../logger/ILogger";
import { Bindable, NotifyProperty, nameof } from "../../../../../shared/bindable-data";
import { WalletDto } from "../../../../networking/data/responses/auth/web3/WalletDto";
import { ExternalWalletType } from "../../../../../web3/externalWallets/ExternalWalletType";
import { PiggyClubItemsDto } from "by/gamefactory/cityversetycoon/networking/data/responses/piggyclub/PiggyClubItemsDto";
import { ObjectFieldsChecker } from "by/gamefactory/tools/ObjectFieldsChecker";

@injectable()
export class MyWalletModel extends Bindable implements IMyWalletModel {
    public readonly wallets: NotifyProperty<Array<WalletDto>>;

    public constructor(
        @inject("INetworkClient")
        private readonly networkClient: INetworkClient,
        private readonly web3Model: Web3Model,
        @inject("ILogger")
        private readonly logger: ILogger
    ) {
        super();
        this.wallets = new NotifyProperty(this, nameof(this, "wallets"), []);
    }

    @initializer()
    public async initialize(): Promise<void> {
        await this.updateWallets();
    }

    public async updateWallets(): Promise<void> {
        const wallets = await this.networkClient.getExternalWallets();
        this.wallets.setValue(wallets.items ?? []);
    }

    public async linkExternalWallet(address: string, signature: string, type: ExternalWalletType): Promise<boolean> {
        try {
            const result = await this.networkClient.linkExternalWallet(address, signature, type.toUpperCase());
            await this.updateWallets();
            return result != null;
        }
        catch (e: unknown) {
            this.logger.error("Exception on unklink external wallet");
        }
    }

    public async unlinkExternalWallet(address: string): Promise<boolean> {
        try {
            const result = await this.networkClient.unlinkExternalWallet(address);
            const unlinked = result.status === "UNLINKED";
            if (unlinked) {
                await this.updateWallets();
            }
            return unlinked;
        }
        catch (e: unknown) {
            this.logger.error("Exception on unklink external wallet");
            return false;
        }
    }

    public async burnPackage(nft: NFTPackage): Promise<RewardDto> {
        const transactionResult = nft instanceof NFTGiftPackage ? await this.web3Model.burnGiftPackage(nft) : await this.web3Model.burnPackage(nft);
        if (transactionResult == null) {
            return null;
        }
        if (typeof transactionResult === "boolean") {
            return transactionResult ? { diceRolls: nft.diceRollsReward } : null;
        }
        switch (nft.packageType) {
            case PackageType.DAILY_WHEEL:
                return await this.networkClient.dailyWheelBurn(transactionResult);
            case PackageType.GOLDEN_WHEEL:
                return await this.networkClient.goldenWheelBurn(transactionResult);
            case PackageType.HOURLY_BONUS:
                return await this.networkClient.hourlyBonusBurn(transactionResult);
            case PackageType.PERSONAL_OFFER:
                const personalOfferResult = await this.networkClient.personalOfferBurn(transactionResult);
                return { diceRolls: personalOfferResult.offer.reward.diceRolls };
            case PackageType.L1_STORE_PACKAGE:
            case PackageType.STORE_OFFER:
                const storeOfferResult = await this.networkClient.storePackageBurn(transactionResult);
                return { diceRolls: storeOfferResult.reward.diceRolls };
            default:
                this.logger.error(`Unable to check status of package burn, unknown type:\n${nft.toString()}`);
        }
    }

    private validatePiggyClubItemsDto(dto: PiggyClubItemsDto) {
        ObjectFieldsChecker.throwIfInvalidFields(dto, [
            "items"
        ]);
    }
}